<template lang="pug">
  .b-crm-board
    CommonSection(title="CRM")
      template(#title)
        HelpPopup
          template(#default)
            h2 Функционал «CRM»
            p Учёт заявок от клиентов по этапам воронки продаж: от первого обращения до завершения сделки или отказа. У каждой компании своя CRM, в ней может быть несколько воронок.
            p Заявки лежат карточками на доске и перетаскиваются между этапами. В карточке — клиент, ответственный, суть обращения, файлы, напоминания и история с комментариями.
            p Из заявки можно создать проект: он привяжется к заявке и к выбранному отделу компании.
          template(#details)
            h3 Основная информация
            p Воронка — это набор этапов, причин отказа и типов услуг, а также список участников. Воронки создаёт и настраивает руководитель компании или его заместитель — кнопками рядом с выбором воронки.
            p Помимо этапов на доске есть две закрывающие колонки — «Сделка завершена» и «Отказ». При переносе в «Отказ» нужно указать причину. Из закрывающих колонок заявку можно вернуть в работу.
            p Заявки фильтруются по поиску, этапам, ответственному и типу услуги, сортируются по дате создания, клиенту и названию. Закрытые сделки и отказы по умолчанию скрыты — включите их в фильтре «Этапы».
            p Открыв заявку, можно сменить этап и ответственного, отредактировать клиента, описать суть обращения, приложить файлы, поставить себе напоминание и оставить комментарий. Напоминание приходит уведомлением и отмечается счётчиком на карточке заявки.

            h3 Важно!
            ul
              li Доступ к CRM есть у руководителя компании, его заместителей и участников воронок. Участник видит только те воронки, в которые добавлен.
              li Управляют воронками — создание, этапы, причины отказа, типы услуг, состав участников — только руководитель компании и заместители.
              li Клиента может завести любой участник, а редактировать — руководящий состав и тот, кто его завёл. Сменить клиента у заявки нельзя: он задаётся при её создании.
              li Удалять заявки руководящий состав может любые, а участник воронки — только те, где он назначен ответственным.
              li Ответственным за проект из заявки может быть руководитель компании, заместитель, а также руководитель или менеджер выбранного отдела.
      template(#button)
        ._buttons
          Btn(xl dark :disabled="!funnel" @click="$emit('openMembers')")
            Icon(name="members" pointer colored)
            | Участники
          Btn(xl dark :disabled="!canAddLead" @click="$emit('addLead')")
            Icon(name="plus" pointer colored)
            | Новая заявка
      ._filters
        ._filters-left
          ._search
            BaseInput(v-model="search" label="Поиск" iconName="search")
          ._select-group.-filters
            BaseSelect._select.-sort(:options="sortOptions" v-model="sort" label="Сортировать по" hideNotSelected)
            BaseSelectMulti._select.-stage(:options="columnOptions" v-model="visibleColumnKeys" label="Этапы")
            BaseSelectMulti._select.-assignee(:options="assigneeOptions" v-model="assignee" label="Ответственный")
            BaseSelectMulti._select.-service-type(
              v-if="serviceTypeOptions.length"
              :options="serviceTypeOptions"
              v-model="serviceType"
              label="Тип услуги"
              )
        ._select-group.-funnel
          BaseSelect._select.-funnel(
            :options="funnelOptions"
            :value="funnel"
            label="Воронка"
            hideNotSelected
            @input="$emit('update:funnel', $event)"
            )
          //- воронками управляют только владелец компании и заместители:
          //- участнику воронка доступна лишь на просмотр
          template(v-if="canManage")
            button._icon-btn(type="button" title="Добавить воронку" @click="$emit('openFunnelForm', null)")
              Icon(name="plus" pointer colored)
            button._icon-btn(
              type="button"
              title="Настроить воронку"
              :class="{ disabled: !funnel }"
              :disabled="!funnel"
              @click="$emit('openFunnelForm', funnel)"
            )
              Icon(name="settings" pointer colored)
    //- Саму доску рендерит родитель: это контейнер (CrmKanbanApi), и ему тут
    //- не место. Отдаём ему то, что посчитали из состояния фильтров
    slot(:leads="visibleLeads" :columns="visibleColumns")
</template>

<script>
import crmModel, { CLOSING_COLUMNS } from '@/model/crm';
import utils from '@/utils/common';

import CommonSection from '@/components/CommonSection.vue';
import HelpPopup from '@/components/base/HelpPopup.vue';
import BaseInput from '@/components/BaseInput.vue';
import BaseSelect from '@/components/BaseSelect.vue';
import BaseSelectMulti from '@/components/BaseSelectMulti.vue';

const SORT_OPTIONS = [
  { value: 'created', label: 'По дате создания' },
  { value: 'client', label: 'По имени клиента' },
  { value: 'title', label: 'По названию заявки' },
];

/**
 * Панель управления доской: заголовок, кнопки и фильтры.
 *
 * Поиск, сортировка, набор видимых колонок и фильтр по ответственному — это
 * состояние экрана, а не данные, поэтому живут здесь, а не в контейнере.
 * Наружу они не выходят вовсе: посчитанные по ним заявки и колонки уходят в
 * слот, где родитель рендерит доску.
 */
export default {
  name: 'CrmBoardUi',
  components: {
    CommonSection, HelpPopup, BaseInput, BaseSelect, BaseSelectMulti,
  },
  props: {
    funnels: {
      type: Array,
      default: () => [],
    },
    // id выбранной воронки; смена уходит наружу через update:funnel
    funnel: {
      type: Number,
      default: null,
    },
    stages: {
      type: Array,
      default: () => [],
    },
    leads: {
      type: Array,
      default: () => [],
    },
    // кого можно назначить ответственным — по ним же и фильтруем
    users: {
      type: Array,
      default: () => [],
    },
    // типы услуг воронки — по ним фильтруем доску
    serviceTypes: {
      type: Array,
      default: () => [],
    },
    // владелец компании или заместитель: только он управляет воронками
    canManage: Boolean,
  },
  data() {
    return {
      search: '',
      sort: 'created',
      assignee: [],
      serviceType: [],
      visibleColumnKeys: [],
      sortOptions: SORT_OPTIONS,
    };
  },
  created() {
    // связка между двумя watcher'ами ниже, а не состояние экрана — реактивным
    // быть не должен, поэтому не в data
    this.resetColumnsOnNextStages = false;
  },
  watch: {
    /**
     * Выбрали другую воронку — набор колонок собираем заново, но не сейчас:
     * этапы новой воронки ещё не загружены. Ставим флаг, а решение принимает
     * watcher stages, когда они приедут.
     */
    funnel() {
      this.resetColumnsOnNextStages = true;
      // типы услуг принадлежат воронке: id из прежней не совпали бы ни с одной
      // заявкой новой, и доска молча опустела бы
      this.serviceType = [];
    },
    /**
     * Какие колонки показывать.
     *
     * Новая воронка — все её этапы, кроме закрывающих. Перезагрузка той же
     * воронки (после правок в настройках) — выбор пользователя сохраняется:
     * снятые этапы остаются снятыми, закрывающие колонки — как были, а этапы,
     * появившиеся в настройках только что, включаются, потому что снять их
     * никто не мог.
     */
    stages(stages, previousStages) {
      if (this.resetColumnsOnNextStages) {
        this.resetColumnsOnNextStages = false;
        this.visibleColumnKeys = stages.map(stage => stage.id);
        return;
      }

      const known = previousStages.map(stage => stage.id);
      const selected = this.visibleColumnKeys;

      this.visibleColumnKeys = [
        ...stages
          .filter(stage => !known.includes(stage.id) || selected.includes(stage.id))
          .map(stage => stage.id),
        ...CLOSING_COLUMNS
          .filter(column => selected.includes(column.id))
          .map(column => column.id),
      ];
    },
  },
  computed: {
    funnelOptions() {
      return this.funnels.map(funnel => ({ value: funnel.id, label: funnel.name }));
    },
    assigneeOptions() {
      return this.users.map(user => ({ value: user['@id'], label: utils.getUserName(user) }));
    },
    serviceTypeOptions() {
      return this.serviceTypes.map(type => ({ value: type.id, label: type.name }));
    },
    /** Этапы воронки плюс две закрывающие колонки. */
    columns() {
      return crmModel.boardColumns(this.stages);
    },
    columnOptions() {
      return this.columns.map(column => ({ value: column.id, label: column.name }));
    },
    /** Пустой фильтр — это «все колонки», а не «ни одной». */
    visibleColumns() {
      if (!this.visibleColumnKeys.length) return this.columns;

      return this.columns.filter(column => this.visibleColumnKeys.includes(column.id));
    },
    visibleLeads() {
      const filtered = crmModel.filterLeads(this.leads, {
        search: this.search,
        assignee: this.assignee,
        serviceType: this.serviceType,
      });

      return crmModel.sortLeads(filtered, this.sort);
    },
    canAddLead() { return !!this.funnel && !!this.stages.length; },
  },
};
</script>

<style scoped lang="scss">
.crm-board {
  &__buttons {
    display: flex;
    gap: 16px;
  }

  &__filters {
    display: flex;
    flex-wrap: wrap;
    align-items: flex-end;
    justify-content: space-between;
    gap: 16px;
    margin-bottom: 30px;
  }

  // search + the sort/stage/assignee select-group sit flush against each
  // other; __filters' own space-between then pushes the funnel/icon-buttons
  // select-group (the other direct child) away to the right
  &__filters-left {
    display: flex;
    flex-wrap: wrap;
    align-items: flex-end;
    gap: 16px;
  }

  &__search {
    width: 205px;
  }

  &__select-group {
    display: flex;
    flex-wrap: wrap;
    gap: 16px;
  }

  &__select-group > * {
    flex: 0 0 auto;
  }

  &__icon-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 38px;
    height: 38px;
    border-radius: 4px;
    background: #D3EAFF;
    color: var(--color-accent);
    flex-shrink: 0;
    flex-grow: 0;
    min-width: 38px;
    cursor: pointer;
    transition: opacity 0.2s;

    svg {
      width: 18px;
      height: 18px;
    }

    &:hover {
      opacity: 0.8;
    }

    &.disabled {
      opacity: 0.5;
      cursor: default;
    }
  }
}

// fixed widths matching the Figma mockup, only above the mobile breakpoint
// where filters stack full-width (see the max-width: 576px block below)
@media (min-width: 577px) {
  .crm-board__select--sort {
    width: 176px;
  }

  .crm-board__select--stage {
    width: 147px;
  }

  .crm-board__select--assignee {
    width: 160px;
  }

  .crm-board__select--service-type {
    width: 160px;
  }

  .crm-board__select--funnel {
    width: 147px;
  }
}

// Планшет и мобильные: фильтры рядами. Первый ряд — поиск на всю ширину и
// воронка с кнопками справа от него, второй — остальные селекты, растянутые
// поровну. Для этого обёртку __filters-left «растворяем» (display: contents):
// поиск и группа селектов становятся прямыми детьми __filters, и их порядок
// задаётся order — иначе воронку было бы не поднять в ряд к поиску
@media (max-width: 1023px) {
  .crm-board__filters-left {
    display: contents;
  }

  .crm-board__search {
    order: 1;
    flex: 1 1 200px;
    width: auto;
  }

  .crm-board__select-group--funnel {
    order: 2;
  }

  .crm-board__select-group--filters {
    order: 3;
    width: 100%;
  }

  // фиксированные ширины из блока выше здесь не нужны: селекты делят ряд
  .crm-board__select-group--filters > * {
    flex: 1 1 140px;
    width: auto;
    min-width: 0;
  }
}

// desktop: search + the sort/stage/assignee selects get noticeably wider
@media (min-width: 1024px) {
  .crm-board__search {
    width: 260px;
  }

  .crm-board__select--sort {
    width: 220px;
  }

  .crm-board__select--stage {
    width: 190px;
  }

  .crm-board__select--assignee {
    width: 210px;
  }

  .crm-board__select--service-type {
    width: 210px;
  }
}

// Мобильные: те же ряды, что на планшете, но селекты в столбик. Воронка
// остаётся строкой: селект тянется на остаток, кнопки держат свои 38px — во
// всю ширину они выглядели бы полосами
@media (max-width: 576px) {
  .crm-board {
    &__buttons {
      flex-direction: column;
    }

    &__filters {
      flex-direction: column;
      align-items: stretch;
    }

    &__search {
      flex: 0 0 auto;
    }

    &__select-group--filters {
      flex-direction: column;
    }

    &__select-group--filters > * {
      flex: 0 0 auto;
      width: 100%;
    }

    &__select-group--funnel {
      flex-wrap: nowrap;
    }

    &__select--funnel {
      flex: 1 1 auto;
      min-width: 0;
    }
  }
}
</style>
